import React, { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import Home from './App';
import PrivateRoute from './components/PrivateRoute';
import { Switch, BrowserRouter as Router, Route } from 'react-router-dom';
import * as authActions from './redux/actions/me';
import store from './store';
import { Provider } from 'react-redux';
import 'antd/dist/antd.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'styles/index.scss';
import * as MeService from 'services/me.service';
import * as Auth from 'services/auth';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import GA4React from 'ga-4-react';
import * as Sentry from '@sentry/browser';

const ga4react = new GA4React('G-K7P2E2ML1K');

const SignIn = React.lazy(() => import('./pages/auth/sign-in'));
const SignUp = React.lazy(() => import('./pages/auth/sign-up'));
const ForgotPassword = React.lazy(() => import('./pages/auth/forgot-password'));
const VerifyEmail = React.lazy(() => import('./pages/auth/verify-email'));
const ChangeEmail = React.lazy(() => import('./pages/auth/change-email'));
const NewPassword = React.lazy(() => import('./pages/auth/new-password'));
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

function Root() {
  const [isLoading, setIsLoading] = useState(true);

  const cleanupLoadingState = () => {
    const el = document.getElementById('ipl-progress-indicator');
    if (el) {
      el.classList.add('available');
      setTimeout(() => {
        el.outerHTML = '';
      }, 2000);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (Auth.IsAuthenticated()) {
      MeService.GetMe()
        .then((me) => {
          store.dispatch(authActions.GetMeSuccess(me));
        })
        .catch((e) => {
          // TODO: err.message === "Network Error" (Should this error should be retried?)
          console.error(e);
        })
        .finally(cleanupLoadingState);
    } else {
      cleanupLoadingState();
    }
  }, []);

  if (isLoading) {
    return <>Loading...</>;
  }

  return (
    <Provider store={store}>
      <Suspense fallback={<Spin indicator={antIcon} />}>
        <Router>
          <Switch>
            <Route path="/sign-in" component={SignIn} />
            <Route path="/sign-up" component={SignUp} />
            <Route path="/forgot-password/:code" component={NewPassword} />
            <Route path="/forgot-password" component={ForgotPassword} />
            <Route path="/confirmation/:code" component={VerifyEmail} />
            <Route path="/change-email/:code" component={ChangeEmail} />
            <PrivateRoute path="/" component={Home} />
          </Switch>
        </Router>
      </Suspense>
    </Provider>
  );
}
(async () => {
  Sentry.init({
    dsn: 'https://24bdb639bddab34f312c22475ce1ed8a@o4505746435080192.ingest.sentry.io/4505746436915200',
    environment: process.env.REACT_APP_ENV,
    denyUrls: ['localhost'],
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['https://app.staging.bugal.com.au', 'https://app.bugal.com.au'],
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });

  if (window.location.href.indexOf('app.bugal.com.au') > 0) {
    await ga4react.initialize();
  }

  ReactDOM.render(<Root />, document.getElementById('root'));
})();
